import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Store, MessageCircle, Tag, Heart, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useMosque } from '../contexts/MosqueContext';
import { toYouTubeEmbed } from '../lib/youtube';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UMKMDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { to } = useMosque();
  const [shop, setShop] = useState(null);
  const [views, setViews] = useState(0);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [slide, setSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/umkm/${id}`);
        if (!active) return;
        setShop(data); setViews(data.view_count || 0); setLikes(data.likes || 0);
        try { const { data: v } = await axios.post(`${API_URL}/api/umkm/${id}/view`); if (active) setViews(v.view_count); } catch (_) {}
      } catch (e) { if (active) setNotFound(true); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [id]);

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Assalamu'alaikum, saya tertarik dengan ${shop.name}`);
    window.open(`https://wa.me/${shop.whatsapp}?text=${message}`, '_blank');
  };
  const handleLike = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/umkm/${id}/like`, {}, { withCredentials: true });
      setLikes(data.likes); setLiked(data.liked);
    } catch (e) {
      if (e.response?.status === 401) toast.error('Silakan masuk untuk menyukai iklan ini');
      else toast.error('Gagal menyukai');
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-700" /></div>;

  if (notFound || !shop) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] px-6 py-8 pb-24 flex flex-col items-center justify-center text-center">
        <Store className="w-16 h-16 text-stone-300 mb-4" />
        <p className="text-text-secondary font-body mb-4">Iklan tidak ditemukan</p>
        <button data-testid="umkm-detail-back-empty" onClick={() => navigate(to('/pasar'))} className="text-emerald-700 font-body font-medium hover:underline">Kembali ke Pasar UMKM</button>
      </div>
    );
  }

  const gallery = (shop.images && shop.images.length ? shop.images : (shop.logo_url ? [shop.logo_url] : []));
  const embed = shop.video_url ? toYouTubeEmbed(shop.video_url) : '';
  const isYouTube = embed && embed.includes('/embed/');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#F8F9FA] pb-32" data-testid="umkm-detail-page">
      <div className="relative h-72 bg-cover bg-center" style={{ backgroundImage: gallery[slide] ? `url('${gallery[slide]}')` : 'none', backgroundColor: '#065F46' }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        <button data-testid="umkm-detail-back-btn" onClick={() => navigate(to('/pasar'))} className="absolute top-5 left-5 w-10 h-10 rounded-full backdrop-blur-md bg-white/30 border border-white/40 flex items-center justify-center text-white"><ArrowLeft className="w-5 h-5" /></button>
        <div className="absolute top-5 right-5 flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md bg-black/30 border border-white/20 text-white text-xs font-body font-medium" data-testid="umkm-detail-views"><Eye className="w-4 h-4" />{views}</div>
        {gallery.length > 1 && (
          <>
            <button onClick={() => setSlide((slide - 1 + gallery.length) % gallery.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/30 backdrop-blur-md text-white flex items-center justify-center" data-testid="umkm-slide-prev"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => setSlide((slide + 1) % gallery.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/30 backdrop-blur-md text-white flex items-center justify-center" data-testid="umkm-slide-next"><ChevronRight className="w-5 h-5" /></button>
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1.5">
              {gallery.map((_, i) => <span key={i} className={`h-2 rounded-full transition-all ${i === slide ? 'w-6 bg-amber-400' : 'w-2 bg-white/60'}`} />)}
            </div>
          </>
        )}
        <div className="absolute bottom-5 left-6 right-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/90 text-emerald-950 text-xs font-semibold"><Tag className="w-3.5 h-3.5" />{shop.category}</span>
            {shop.location && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black/30 backdrop-blur-md text-white text-xs font-medium"><MapPin className="w-3.5 h-3.5" />{shop.location}</span>}
          </div>
          <h1 className="text-3xl font-heading font-bold text-white tracking-tight" data-testid="umkm-detail-name">{shop.name}</h1>
        </div>
      </div>

      <div className="px-6 py-6 space-y-5">
        <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-heading font-semibold text-primary-dark">Tentang Usaha</h2>
            <button data-testid="umkm-like-btn" onClick={handleLike} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${liked ? 'bg-rose-500 text-white' : 'bg-rose-50 text-rose-600 hover:bg-rose-100'}`}>
              <Heart className={`w-4 h-4 ${liked ? 'fill-white' : ''}`} /> {likes}
            </button>
          </div>
          <p className="text-sm font-body text-text-secondary leading-relaxed whitespace-pre-line" data-testid="umkm-detail-description">{shop.description}</p>
        </div>

        {embed && (
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-4" data-testid="umkm-detail-video">
            <h2 className="text-lg font-heading font-semibold text-primary-dark mb-3 px-2">Video</h2>
            {isYouTube ? (
              <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingTop: '56.25%' }}>
                <iframe className="absolute inset-0 w-full h-full" src={embed} title="video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <a href={shop.video_url} target="_blank" rel="noreferrer" className="block text-center py-3 bg-emerald-50 text-emerald-700 rounded-2xl font-medium">Lihat Video di Sumbernya</a>
            )}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40">
        <div className="max-w-md mx-auto px-6 pb-6 pt-3 bg-gradient-to-t from-[#F8F9FA] via-[#F8F9FA] to-transparent">
          <motion.button data-testid="umkm-detail-whatsapp-btn" onClick={handleWhatsApp} whileTap={{ scale: 0.97 }} className="relative w-full overflow-hidden rounded-2xl py-4 flex items-center justify-center gap-3 text-white font-body font-semibold shadow-lg" style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
            <MessageCircle className="w-6 h-6 relative z-10" />
            <span className="relative z-10 text-base">Pesan via WhatsApp</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default UMKMDetailPage;
